/* eslint-disable no-param-reassign */
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ERROR_MSGS } from './error-msgs.const';
import { ToastService } from '../services/toast.service';

@Injectable()
export class ErrorHandlerInterceptor implements HttpInterceptor {
  private message$: Subject<string> = new Subject<string>();

  constructor(
    private router: Router,
    private toastService: ToastService,
  ) {
    this.subscribeOnErrorMsg();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: any) => {
        if (err.status === 401) {
          this.router.navigateByUrl('/auth/login');
        } else if (err.status === 500) {
          this.parseBlob(err).then((error: any) => {
            const errorMsg = this.getErrorMessageText(error);
            if (errorMsg) {
               this.toastService.showToast(errorMsg || ERROR_MSGS.defaultErrorMessage);
              if (errorMsg === ERROR_MSGS.expiredDelivery) {
                this.router.navigateByUrl('/inbox/current');
              }
            } else {
              this.router.navigateByUrl('/inbox/current');
            }
          });
        } else {
          this.getErrorMessage(err);
        }
        throw err;
      }),
    );
  }

  getErrorMessage(errorRsp: HttpErrorResponse) {
    const status = (errorRsp && errorRsp.status) || '';
    const statusText = (errorRsp && errorRsp.statusText) || '';
    let defaultText = 'HTTP Error happened:';
    if (status && statusText) {
      defaultText += ` ${status} ${statusText}`;
    } else {
      defaultText = ERROR_MSGS.defaultErrorMessage;
    }

    this.fetchGeneralError(errorRsp, defaultText);
    this.fetchBlobError(errorRsp, defaultText);
    this.fetchStringError(errorRsp, defaultText);
    this.fetchStringMessageError(errorRsp);
  }

  async parseBlob(err: any) {
    // eslint-disable-next-line consistent-return
    return await new Promise((resolve: any) => {
      if (err && err.error instanceof Blob) {
        const fr: any = new FileReader();
        fr.onload = (evt: any) => {
          err.error = evt.target.result;
          return resolve(err);
        };
        fr.readAsText(err.error);
      } else {
        err.error = err && typeof err.error === 'object' ? JSON.stringify(err.error) : err.error;
        return resolve(err);
      }
    });
  }

  getErrorMessageText(error): string {
    const errorRespMsg = error && error.error;

    if (
      (this.isDocOrImg(errorRespMsg) && this.isDocOrImgDownloadAll(errorRespMsg)) ||
      (this.isAudioOrVideo(errorRespMsg) && this.isAudioOrVideoDownloadAll(errorRespMsg)) ||
      this.isDownloadAllErrorMessage(error.url, errorRespMsg)
    ) {
      return ERROR_MSGS.donwloadAllLimitMessage;
    }

    return (
      this.getErrorMsg(errorRespMsg, 'streamingLimitMessage') ||
      this.getErrorMsg(errorRespMsg, 'donwloadLimitMessage') ||
      this.getErrorMsg(errorRespMsg, 'donwloadAsperaMessage') ||
      this.getErrorMsg(errorRespMsg, 'donwloadDeniedDeviceAllMessage') ||
      this.getErrorMsg(errorRespMsg, 'expiredDelivery') ||
      this.getErrorMsg(errorRespMsg, 'shareDeliveryErrorMessage') ||
      ERROR_MSGS.defaultErrorMessage
    );
  }

  isDocOrImg(error: string): boolean {
    return error.search(/\/DOC\//) !== -1 || error.search(/\/IMG\//) !== -1;
  }

  isAudioOrVideo(error: string): boolean {
    return (
      error.search(/\/download\/dwl\//) !== -1 &&
      (error.search(/\/AUDIO\//) !== -1 || error.search(/\/VIDEO\//) !== -1)
    );
  }

  isDocOrImgDownloadAll(error: string): boolean {
    return (
      error.search(/\/download\/dwl\//) !== -1 &&
      (error.search(/\/DOC\//) !== -1 || error.search(/\/IMG\//) !== -1)
    );
  }

  isAudioOrVideoDownloadAll(error: string): boolean {
    return error.search(/\/dwl"/) === -1 && error.search(/\/dwl\//) !== -1;
  }

  isDownloadAllErrorMessage(url: string, errorMsg: string): boolean {
    return (
      url.endsWith('campaignTrackId=') &&
      (errorMsg.includes(ERROR_MSGS.donwloadLimitMessage) ||
        errorMsg.includes(ERROR_MSGS.donwloadAllLimitMessage))
    );
  }

  private getErrorMsg(errorMsg: string, errorKey: string): string {
    return errorMsg.includes(ERROR_MSGS[errorKey]) && ERROR_MSGS[errorKey];
  }

  private fetchGeneralError(errorRsp, defaultText) {
    if (!errorRsp.error) {
      this.message$.next(defaultText);
    }
  }

  private fetchBlobError(errorRsp, defaultText) {
    if (errorRsp.error instanceof Blob) {
      const fr = new FileReader();

      fr.onload = async () => {
        try {
          const errorMsg = JSON.parse(<string>fr.result);
          this.message$.next(errorMsg.message || defaultText);
        } catch (err) {
          this.message$.next(<string>fr.result || defaultText);
        }
      };

      fr.readAsText(errorRsp.error);
    }
  }

  private fetchStringError(errorRsp, defaultText) {
    if (errorRsp && typeof errorRsp.error === 'string') {
      const errorMsg = JSON.parse(errorRsp.error);
      this.message$.next(errorMsg.message || defaultText);
    }
  }

  private fetchStringMessageError(errorRsp) {
    if (errorRsp && errorRsp.error && errorRsp.error.message) {
      this.message$.next(errorRsp.error.message);
    }
  }

  private subscribeOnErrorMsg() {
    this.message$.asObservable().subscribe((errorMsg: string) => {
       this.toastService.showToast(errorMsg || ERROR_MSGS.defaultErrorMessage);
    });
  }
}
